/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Property } from 'csstype';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import {
    RESIZE_HANDLE_DEPTH_ONE_SIZE,
    RESIZE_HANDLE_SIZE,
    SHAPE_DEPTH_ONE_THICKNESS,
    SHAPE_THICKNESS
} from 'src/modules/labeling/utils/labelingConstants';

const { styleDiv } = getStylistV2('Renderers');

export const ResizeHandle = styleDiv(
    'ResizeHandle',
    (_, cssVars) =>
        ({
            top: cssVars.top,
            bottom: cssVars.bottom,
            left: cssVars.left,
            right: cssVars.right,
            backgroundColor: cssVars.backgroundColor,
            opacity: cssVars.opacity,
            height: cssVars.size,
            width: cssVars.size,
            position: 'absolute',
            transition: 'all 200ms ease-in',
            cursor: cssVars.cursor,
            pointerEvents: cssVars.pointerEvents as Property.PointerEvents,
            $nest: {
                '&:hover': {
                    transform: cssVars.transformOnHover,
                    opacity: '1'
                }
            }
        } as Record<string, any>),
    {
        cssVars: {
            size: '',
            backgroundColor: '',
            cursor: '',
            top: '',
            left: '',
            right: '',
            opacity: '',
            bottom: '',
            transformOnHover: '',
            pointerEvents: ''
        },
        cssVarsSetter: ({
            color,
            cursor = 'horizontal',
            disabled = false,
            hidden = false,
            mode = 'label',
            position = 'start',
            variant
        }: {
            color: string;
            cursor?: 'horizontal' | 'diagonal';
            disabled?: boolean;
            hidden?: boolean;
            mode?: 'label' | 'prediction';
            position?: 'start' | 'end';
            variant: 'large' | 'small';
        }) => {
            const size = variant === 'large' ? RESIZE_HANDLE_DEPTH_ONE_SIZE : RESIZE_HANDLE_SIZE;
            const shapeThickness = variant === 'large' ? SHAPE_DEPTH_ONE_THICKNESS : SHAPE_THICKNESS;

            const offsetX = mode === 'label' ? '0' : '-2px';
            const offsetY = `-${size + (shapeThickness - size) / 2}px`;

            const left = position === 'start' ? offsetX : 'unset';
            const right = position === 'end' ? offsetX : 'unset';
            const top = mode === 'label' || (mode === 'prediction' && position === 'start') ? offsetY : 'unset';
            const bottom = mode === 'prediction' && position === 'end' ? offsetY : 'unset';
            const actualHidden = mode === 'label' ? hidden : hidden || disabled;
            const curserStyle = cursor === 'horizontal' ? 'ew-resize' : 'nwse-resize';

            return {
                left,
                right,
                top,
                bottom,
                size: `${size}px`,
                backgroundColor: color,
                cursor: disabled ? 'unset' : curserStyle,
                opacity: actualHidden ? '0' : '1',
                transformOnHover: disabled ? 'scale(1)' : 'scale(2)',
                pointerEvents: disabled ? 'none' : 'unset'
            };
        }
    }
);
